/**
 * The client half of the chat status: reading what a conversation created (extracted on the server by
 * common-lib's `extractConversationCreations`, which says why it reads TOOL_USE rows) and the rule that matches
 * those names against the live lists the chat's status draws.
 *
 * No runtime import reaches common-lib, on purpose (the type import is erased): the tests run this, and a runtime
 * import that reaches common-lib fails the suite in CI (see orion-web/CLAUDE.md, "Tests").
 */
import type { ConversationCreations } from '@wix/ai-scheduling-assistant-common-lib/utils';
import { workspaceFileRefOf } from './vfs-attachment';

export type { ConversationCreations };

export const EMPTY_CREATIONS: ConversationCreations = {
  spaces: [],
  agents: [],
  routines: [],
  connectors: [],
  files: [],
  knowledge: [],
  pendingBands: [],
};

type Input = Record<string, unknown>;

const record = (value: unknown): Input | undefined =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Input) : undefined;
const list = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

/** Reads a route payload defensively: a field that is not a string list is treated as empty. */
export function readConversationCreations(value: unknown): ConversationCreations {
  const source = record(value);
  const strings = (field: unknown) => list(field).filter((item): item is string => typeof item === 'string' && item.length > 0);
  return {
    spaces: strings(source?.spaces),
    agents: strings(source?.agents),
    routines: strings(source?.routines),
    connectors: strings(source?.connectors),
    files: strings(source?.files),
    knowledge: strings(source?.knowledge),
    pendingBands: list(source?.pendingBands).flatMap((value) => {
      const band = record(value);
      const name = typeof band?.name === 'string' ? band.name : '';
      const agents = list(band?.agents).flatMap((agentValue) => {
        const agent = record(agentValue);
        const agentName = typeof agent?.name === 'string' ? agent.name : '';
        return agentName ? [{ name: agentName, ...(typeof agent?.title === 'string' && agent.title ? { title: agent.title } : {}) }] : [];
      });
      return name && agents.length > 0 ? [{ name, agents }] : [];
    }),
  };
}

export interface LiveWorkspace {
  id: string;
  slug: string;
}
export interface LiveAgent {
  id: string;
  name: string;
}
export interface LiveRoutine {
  id: string;
  name?: string;
}
export interface LiveGoal<G> {
  id: string;
  conversationId?: string;
  members?: G[];
  tasks?: { id: string }[];
}
export interface LiveConnector {
  toolkit: string;
  name?: string;
}
export interface LiveTask {
  id: string;
  conversationId?: string;
  goalId?: string;
}
export interface LiveFile {
  name: string;
  workspaceSlug?: string;
  isApp?: boolean;
}
export interface LiveKnowledge {
  path: string;
}

export interface ChatStatus<W, A, R, G, C, T = unknown, F = unknown, K = unknown> {
  spaces: W[];
  agents: A[];
  routines: R[];
  goals: G[];
  connectors: C[];
  tasks: T[];
  /** The files it wrote, each beside the vfsPath an attachment of it would carry. */
  files: { vfsPath: string; file: F }[];
  knowledge: K[];
  /** The bands an open offer puts to the user: agents nobody has hired yet, so there is no live list to check. */
  pendingBands: ConversationCreations['pendingBands'];
}

type AnyChatStatus = ChatStatus<unknown, unknown, unknown, unknown, unknown, unknown, unknown, unknown>;

const key = (value: string) => value.trim().toLowerCase();

/**
 * The live entities behind a conversation's creations, in the order the conversation made them.
 *
 * - A routine counts only when this conversation created it by name. Living in a space this conversation
 *   created is not enough: a later conversation can add routines to the same space.
 * - A goal counts when it records this conversation as its own (`conversationId`). A member goal is listed
 *   only when its parent is not, so a plan never shows twice.
 * - Agents match by name, case-insensitively: that is the only handle a create call has, so an agent
 *   renamed since drops out.
 * - A task counts when it records this conversation as the one that started it, unless it belongs to a goal
 *   listed here, which already shows it.
 * - A file counts while the space still holds it at the path it was written to; knowledge, while the entry
 *   is still there.
 */
export function resolveChatStatus<
  W extends LiveWorkspace,
  A extends LiveAgent,
  R extends LiveRoutine,
  G extends LiveGoal<G>,
  C extends LiveConnector,
  T extends LiveTask = LiveTask,
  F extends LiveFile = LiveFile,
  K extends LiveKnowledge = LiveKnowledge,
>(
  conversationId: string,
  creations: ConversationCreations,
  live: { workspaces: W[]; agents: A[]; routines: R[]; goals: G[]; connectors: C[]; tasks?: T[]; files?: F[]; knowledge?: K[] },
): ChatStatus<W, A, R, G, C, T, F, K> {
  const pick = <T>(names: string[], items: T[], keysOf: (item: T) => (string | undefined)[]): T[] => {
    const out: T[] = [];
    for (const name of names) {
      const wanted = key(name);
      const match = items.find((item) => keysOf(item).some((candidate) => candidate !== undefined && key(candidate) === wanted));
      if (match && !out.includes(match)) out.push(match);
    }
    return out;
  };

  const goals: G[] = [];
  for (const goal of live.goals) {
    if (goal.conversationId === conversationId) {
      goals.push(goal);
      continue;
    }
    for (const member of goal.members ?? []) {
      if (member.conversationId === conversationId) goals.push(member);
    }
  }

  // A goal is a set of tasks, so a task its card already lists stays off the Tasks card: matched by the goal it
  // records, and by id, for a goal task that carries no goal id.
  const shownGoals = goals.flatMap((goal) => [goal, ...(goal.members ?? [])]);
  const shownGoalIds = new Set(shownGoals.map((goal) => goal.id));
  const goalTaskIds = new Set(shownGoals.flatMap((goal) => (goal.tasks ?? []).map((task) => task.id)));
  const tasks = (live.tasks ?? []).filter(
    (task) =>
      task.conversationId === conversationId && !goalTaskIds.has(task.id) && !(task.goalId && shownGoalIds.has(task.goalId)),
  );

  const files: { vfsPath: string; file: F }[] = [];
  for (const vfsPath of creations.files) {
    const ref = workspaceFileRefOf(vfsPath);
    const file = ref && (live.files ?? []).find((item) => !item.isApp && item.workspaceSlug === ref.slug && item.name === ref.name);
    if (file) files.push({ vfsPath, file });
  }

  const knowledge = pick(creations.knowledge, live.knowledge ?? [], (entry) => [entry.path]);

  return {
    spaces: pick(creations.spaces, live.workspaces, (workspace) => [workspace.slug]),
    agents: pick(creations.agents, live.agents, (agent) => [agent.name]),
    routines: pick(creations.routines, live.routines, (routine) => [routine.name]),
    goals,
    connectors: pick(creations.connectors, live.connectors, (connector) => [connector.toolkit, connector.name]),
    tasks,
    files,
    knowledge,
    pendingBands: creations.pendingBands,
  };
}

/**
 * Whether the agents island has anything to show: everything but the space, which the island design leaves out,
 * and the offered bands, which only the desktop panel shows (a phone shows agents once they are hired). The
 * phones' twin rule gates their island the same way.
 */
export function islandStatusHasContent(status: AnyChatStatus): boolean {
  return (
    status.agents.length +
      status.routines.length +
      status.goals.length +
      status.connectors.length +
      status.tasks.length +
      status.files.length +
      status.knowledge.length >
    0
  );
}

/** Whether the panel has anything of its own to show. Attachments alone do not open it: they have the ⋮ menu. */
export function chatStatusHasContent(status: AnyChatStatus): boolean {
  return (
    status.spaces.length +
      status.agents.length +
      status.routines.length +
      status.goals.length +
      status.connectors.length +
      status.tasks.length +
      status.files.length +
      status.knowledge.length +
      status.pendingBands.length >
    0
  );
}
